import { useTranslation } from "react-i18next";
import type {
  AnalyticsKpiMetric,
  AnalyticsLiveKpis,
  AnalyticsSnapshotResponse,
} from "@/features/admin/adminAnalytics.types";
import { KPICard } from "@/features/admin/components/analytics/KPICard";
import {
  MetricBarChart,
  MetricLineChart,
} from "@/features/admin/components/analytics/MetricCharts";

type OverviewAnalyticsTabProps = {
  liveKPIs: AnalyticsLiveKpis | null;
  loading?: boolean;
  snapshot: AnalyticsSnapshotResponse | null;
};

function buildOverviewKpis(
  liveKPIs: AnalyticsLiveKpis | null,
  snapshot: AnalyticsSnapshotResponse | null,
  labels: Record<string, string>,
): AnalyticsKpiMetric[] {
  return [
    {
      currentState: true,
      key: "totalRegisteredUsers",
      label: labels.totalRegisteredUsers,
      unit: "count",
      value: liveKPIs?.totalRegisteredUsers ?? null,
    },
    {
      currentState: false,
      key: "activeUsers",
      label: labels.activeUsers,
      unit: "count",
      value: null,
    },
    {
      currentState: true,
      key: "paidSubscribers",
      label: labels.paidSubscribers,
      unit: "count",
      value: liveKPIs?.paidSubscribers ?? null,
    },
    {
      currentState: true,
      key: "currentMrrCents",
      label: labels.currentMrr,
      unit: "cents",
      value: liveKPIs?.currentMrrCents ?? null,
    },
    {
      currentState: false,
      key: "creditsConsumed",
      label: labels.creditsConsumed,
      unit: "credits",
      value: snapshot?.series.creditsConsumedPerDay?.reduce((total, point) => total + Number(point.credits ?? 0), 0) ?? null,
    },
    {
      currentState: true,
      key: "estimatedAiCost",
      label: labels.estimatedAiCost,
      unit: "usd",
      value: liveKPIs?.aiBudgetStatus.spentUSD ?? null,
    },
    {
      currentState: true,
      key: "openPqrTickets",
      label: labels.openPqrTickets,
      unit: "count",
      value: liveKPIs?.openPqrTickets ?? null,
    },
    {
      currentState: true,
      key: "runningImportJobs",
      label: labels.runningImportJobs,
      unit: "count",
      value: liveKPIs?.runningImportJobs ?? null,
    },
  ];
}

export function OverviewAnalyticsTab({ liveKPIs, loading, snapshot }: OverviewAnalyticsTabProps) {
  const { t } = useTranslation();
  const kpis = buildOverviewKpis(liveKPIs, snapshot, {
    activeUsers: t("adminWorkspace.analytics.kpis.activeUsers"),
    creditsConsumed: t("adminWorkspace.analytics.kpis.creditsConsumed"),
    currentMrr: t("adminWorkspace.analytics.kpis.currentMrr"),
    estimatedAiCost: t("adminWorkspace.analytics.kpis.estimatedAiCost"),
    openPqrTickets: t("adminWorkspace.analytics.kpis.openPqrTickets"),
    paidSubscribers: t("adminWorkspace.analytics.kpis.paidSubscribers"),
    runningImportJobs: t("adminWorkspace.analytics.kpis.runningImportJobs"),
    totalRegisteredUsers: t("adminWorkspace.analytics.kpis.totalRegisteredUsers"),
  });

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpis.map((metric) => (
          <KPICard key={metric.key} metric={metric} />
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <section className="rounded-[1.45rem] border border-white/10 bg-black/20 p-4">
          <h3 className="text-sm font-semibold text-[#fff7ef]">
            {t("adminWorkspace.analytics.overview.newUsersChart")}
          </h3>
          <div className="mt-4">
            <MetricLineChart
              data={snapshot?.series.newUsersPerDay ?? []}
              emptyMessage={t("adminWorkspace.analytics.empty")}
              loading={loading}
              series={["users"]}
            />
          </div>
        </section>

        <section className="rounded-[1.45rem] border border-white/10 bg-black/20 p-4">
          <h3 className="text-sm font-semibold text-[#fff7ef]">
            {t("adminWorkspace.analytics.overview.creditsChart")}
          </h3>
          <div className="mt-4">
            <MetricBarChart
              data={snapshot?.series.creditsConsumedPerDay ?? []}
              emptyMessage={t("adminWorkspace.analytics.empty")}
              loading={loading}
              series={["credits"]}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
